import type { ReactNode } from 'react';
import { useQueries } from '@tanstack/react-query';
import { isGivmoError } from '@givmo/api-client';
import {
  Button,
  EmptyState,
  MODULE_SQUARE_NO_ACCESS_LINE,
  Skeleton,
  shouldInterceptClick,
} from '@givmo/design-system';
import { OrgConsoleUnavailableNotice, OrgNotPermittedNotice } from '@givmo/org-console';
import { useFundraisersModule } from './fundraisers-context';
import { fundraiserOptions } from './fundraisers-queries';
import { FUNDRAISERS_ROUTES } from './fundraisers-routes';

export const FUNDRAISERS_UNAVAILABLE = 'Fundraisers aren’t available for this organization here.';

/** Inner authorization stays inside the host's permitted ModuleGate children. */
export function FundraisersReadState({
  error,
  denied = false,
  detail = false,
  fundraiserId,
  onRetry,
}: {
  error?: unknown;
  denied?: boolean;
  detail?: boolean;
  /** The page's fundraiser: a miss asks the person's other organizations for it. */
  fundraiserId?: string;
  onRetry: () => void;
}) {
  if (denied || (isGivmoError(error) && error.status === 403)) {
    return (
      <OrgNotPermittedNotice
        testId="fundraisers-locked"
        title="Fundraisers"
        description={MODULE_SQUARE_NO_ACCESS_LINE}
      />
    );
  }
  if (isGivmoError(error) && error.status === 404) {
    return detail ? (
      <FundraiserNotHere fundraiserId={fundraiserId} />
    ) : (
      <OrgConsoleUnavailableNotice
        testId="fundraisers-unavailable"
        title="Fundraisers"
        description={FUNDRAISERS_UNAVAILABLE}
      />
    );
  }
  return (
    <EmptyState
      testId={detail ? 'fundraiser-error' : 'fundraisers-error'}
      title="Couldn’t load fundraisers"
      description="Please try again."
      action={
        <Button appearance="saas" tone="secondary" testId="fundraisers-retry" onClick={onRetry}>
          Try again
        </Button>
      }
    />
  );
}

/** At most this many other organizations are asked for a fundraiser this one doesn't hold. */
const MAX_ORGANIZATIONS_ASKED = 10;

/**
 * A fundraiser this organization doesn't hold. A link opened while another of the person's
 * organizations is active in this tab names a fundraiser of THAT organization: each of the person's
 * other organizations is asked (one read each, never retried), and the first that holds it is offered
 * ("This fundraiser is in Kennedy Children's Center" · Switch organization). Switching
 * is the person's act, never done for them: a page that quietly changed the organization in force would
 * be the cross-tab surprise this exists to end. No other organization holds it (or none was asked): not
 * found, with the way back.
 */
export function FundraiserNotHere({ fundraiserId }: { fundraiserId?: string }) {
  const config = useFundraisersModule();
  const others =
    fundraiserId === undefined
      ? []
      : (config.otherOrganizations?.organizations ?? [])
          .filter((org) => org.orgRef !== config.orgRef)
          .slice(0, MAX_ORGANIZATIONS_ASKED);
  const asked = useQueries({
    queries: others.map((org) => ({
      ...fundraiserOptions(org.orgRef, fundraiserId ?? ''),
      retry: false,
    })),
  });
  const holder = others.find((_, index) => asked[index]?.isSuccess === true);
  if (holder === undefined && asked.some((read) => read.isPending)) return <FundraisersLoading />;
  if (holder !== undefined && config.otherOrganizations) {
    const { switchTo } = config.otherOrganizations;
    return (
      <EmptyState
        testId="fundraiser-in-other-organization"
        title={`This fundraiser is in ${holder.name}`}
        action={
          <Button
            appearance="saas"
            testId="fundraiser-switch-organization"
            onClick={() => switchTo(holder.orgRef)}
          >
            Switch organization
          </Button>
        }
      />
    );
  }
  return (
    <EmptyState
      testId="fundraiser-not-found"
      title="This fundraiser isn’t available"
      action={
        <ModuleLink href={FUNDRAISERS_ROUTES.list} testId="fundraiser-back-from-missing">
          Back to Fundraisers
        </ModuleLink>
      }
    />
  );
}

export function FundraisersLoading() {
  return (
    <div
      data-testid="fundraisers-loading"
      className="flex flex-col gap-16"
      role="status"
      aria-label="Loading fundraisers"
    >
      <Skeleton className="h-32 w-[256px]" />
      <Skeleton className="h-[180px] w-full rounded-lg" />
    </div>
  );
}

export function ModuleLink({
  href,
  children,
  testId,
  className,
}: {
  href: string;
  children: ReactNode;
  testId: string;
  className?: string;
}) {
  const config = useFundraisersModule();
  return (
    <a
      href={href}
      className={
        className ??
        'inline-flex min-h-44 items-center text-s14 font-w600 text-brand-700 hover:underline'
      }
      data-testid={testId}
      onClick={(event) => {
        if (shouldInterceptClick(event)) {
          event.preventDefault();
          config.onNavigate(href);
        }
      }}
    >
      {children}
    </a>
  );
}

export function FundraisersPageShell({
  children,
  testId,
}: {
  children: ReactNode;
  testId: string;
}) {
  const config = useFundraisersModule();
  return (
    <div
      data-testid={testId}
      data-module-prefix={config.testIdPrefix}
      className="fundraisers-module mx-auto flex w-full max-w-[1200px] flex-col gap-20 px-16 py-24 sm:px-24"
    >
      {children}
    </div>
  );
}
